import React, { useState, useRef, useEffect } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GastoForm = ({
  open,
  onClose,
  formData,
  setFormData,
  gastoEditando,
  categorias,
  proveedores,
  mediosPago,
  archivoComprobante,
  setArchivoComprobante,
  onGuardar,
  onProveedorCreado,
  onCategoriaCreada,
  onMedioPagoCreado
}) => {
  const [proveedorBusqueda, setProveedorBusqueda] = useState('');
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const [mostrarModalProveedor, setMostrarModalProveedor] = useState(false);
  const [mostrarModalCategoria, setMostrarModalCategoria] = useState(false);
  const [mostrarModalMedioPago, setMostrarModalMedioPago] = useState(false);
  
  // Estados para nuevo proveedor
  const [nuevoProveedorNombre, setNuevoProveedorNombre] = useState('');
  const [nuevoProveedorCategoria, setNuevoProveedorCategoria] = useState('');
  
  // Estado para nueva categoría
  const [nuevaCategoriaNombre, setNuevaCategoriaNombre] = useState('');
  
  // Estado para nuevo medio de pago
  const [nuevoMedioPagoNombre, setNuevoMedioPagoNombre] = useState('');
  
  const inputRef = useRef(null);
  const sugerenciasRef = useRef(null);

  // Sincronizar búsqueda con proveedor seleccionado
  useEffect(() => {
    if (formData.proveedor) {
      setProveedorBusqueda(formData.proveedor);
    } else {
      setProveedorBusqueda('');
    }
  }, [formData.proveedor, open]);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sugerenciasRef.current && !sugerenciasRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setMostrarSugerencias(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [filtroCategoriaBusqueda, setFiltroCategoriaBusqueda] = useState('todas');
  
  // Filtrar proveedores por búsqueda
  const proveedoresSugeridos = (proveedorBusqueda.trim().length > 0 || filtroCategoriaBusqueda !== 'todas')
    ? proveedores
        .filter(p => p.nombre.toLowerCase().includes(proveedorBusqueda.toLowerCase()))
        .filter(p => filtroCategoriaBusqueda === 'todas' || p.categoria_id === filtroCategoriaBusqueda)
        .sort((a, b) => a.nombre.localeCompare(b.nombre))
        .slice(0, 6)
    : [];

  const seleccionarProveedor = (prov) => {
    setFormData({ 
      ...formData, 
      proveedor_id: prov.id_proveedor, 
      proveedor: prov.nombre,
      categoria_id: prov.categoria_id || ''
    });
    setProveedorBusqueda(prov.nombre);
    setMostrarSugerencias(false);
  };

  const handleProveedorInputChange = (e) => {
    const value = e.target.value;
    setProveedorBusqueda(value);
    setFormData({ ...formData, proveedor: value, proveedor_id: '', categoria_id: '' });
    setMostrarSugerencias(true);
  };

  const abrirModalNuevoProveedor = () => {
    setNuevoProveedorNombre(proveedorBusqueda);
    setNuevoProveedorCategoria('');
    setMostrarSugerencias(false);
    setMostrarModalProveedor(true);
  };

  const crearNuevoProveedor = async () => {
    if (!nuevoProveedorNombre.trim()) {
      toast.error('Ingrese el nombre del proveedor');
      return;
    }
    if (!nuevoProveedorCategoria) {
      toast.error('Seleccione una categoría para el proveedor');
      return;
    }

    try {
      const res = await axios.post(`${API}/proveedores`, {
        nombre: nuevoProveedorNombre.trim(),
        categoria_id: nuevoProveedorCategoria
      });
      
      const nuevoProveedor = res.data;
      toast.success('Proveedor creado exitosamente');
      
      // Notificar al padre
      if (onProveedorCreado) {
        onProveedorCreado(nuevoProveedor);
      }
      
      // Seleccionar el nuevo proveedor
      setFormData({
        ...formData,
        proveedor_id: nuevoProveedor.id_proveedor,
        proveedor: nuevoProveedor.nombre,
        categoria_id: nuevoProveedor.categoria_id
      });
      setProveedorBusqueda(nuevoProveedor.nombre);
      
      setMostrarModalProveedor(false);
      setNuevoProveedorNombre('');
      setNuevoProveedorCategoria('');
    } catch (error) {
      toast.error('Error al crear el proveedor');
    }
  };

  const crearNuevaCategoria = async () => {
    if (!nuevaCategoriaNombre.trim()) {
      toast.error('Ingrese el nombre de la categoría');
      return;
    }

    try {
      const res = await axios.post(`${API}/categorias`, {
        nombre: nuevaCategoriaNombre.trim()
      });
      
      const nuevaCategoria = res.data;
      toast.success('Categoría creada exitosamente');
      
      // Notificar al padre
      if (onCategoriaCreada) {
        onCategoriaCreada(nuevaCategoria);
      }
      
      // Seleccionar la nueva categoría en el modal de proveedor
      setNuevoProveedorCategoria(nuevaCategoria.id_categoria);
      
      setMostrarModalCategoria(false);
      setNuevaCategoriaNombre('');
    } catch (error) {
      toast.error('Error al crear la categoría');
    }
  };

  const crearNuevoMedioPago = async () => {
    if (!nuevoMedioPagoNombre.trim()) {
      toast.error('Ingrese el nombre del medio de pago');
      return;
    }

    try {
      const res = await axios.post(`${API}/medios-pago`, {
        nombre: nuevoMedioPagoNombre.trim(),
        activo: true
      });
      
      const nuevoMedioPago = res.data;
      toast.success('Medio de pago creado exitosamente');
      
      // Notificar al padre
      if (onMedioPagoCreado) {
        onMedioPagoCreado(nuevoMedioPago);
      }
      
      // Seleccionar el nuevo medio de pago
      setFormData({
        ...formData,
        medio_pago_id: nuevoMedioPago.id_medio_pago
      });
      
      setMostrarModalMedioPago(false);
      setNuevoMedioPagoNombre('');
    } catch (error) {
      toast.error('Error al crear el medio de pago');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {gastoEditando ? 'Editar Gasto' : 'Nuevo Gasto'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Fecha */}
              <div>
                <Label htmlFor="fecha" className="text-[#0E3A53]">Fecha *</Label>
                <Input
                  id="fecha"
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                  data-testid="input-fecha"
                />
              </div>

              {/* Monto */}
              <div>
                <Label htmlFor="monto" className="text-[#0E3A53]">Monto *</Label>
                <Input
                  id="monto"
                  type="number"
                  value={formData.monto || ''}
                  onChange={(e) => setFormData({ ...formData, monto: parseFloat(e.target.value) || 0 })}
                  className="text-right"
                  placeholder="Ingrese monto"
                  data-testid="input-monto"
                />
              </div>
            </div>

            {/* Proveedor con autocompletado */}
            <div className="relative">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[#0E3A53]">Proveedor *</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={abrirModalNuevoProveedor}
                  className="text-[#1AA1D8] text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Nuevo Proveedor
                </Button>
              </div>
              <div className="flex gap-2 relative">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    ref={inputRef}
                    type="text"
                    value={proveedorBusqueda}
                    onChange={handleProveedorInputChange}
                    onFocus={() => setMostrarSugerencias(true)}
                    placeholder="Escriba para buscar proveedor..."
                    className="pl-9"
                    data-testid="input-proveedor"
                  />
                  {proveedorBusqueda && (
                    <button
                      type="button"
                      onClick={() => {
                        setProveedorBusqueda('');
                        setFormData({ ...formData, proveedor: '', proveedor_id: '', categoria_id: '' });
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Select 
                  value={filtroCategoriaBusqueda} 
                  onValueChange={setFiltroCategoriaBusqueda}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Categoría..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas las categorías</SelectItem>
                    {[...categorias].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(cat => (
                      <SelectItem key={cat.id_categoria} value={cat.id_categoria}>
                        {cat.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Sugerencias de proveedores */}
              {mostrarSugerencias && (proveedoresSugeridos.length > 0 || proveedorBusqueda.trim()) && (
                <div 
                  ref={sugerenciasRef}
                  className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto"
                >
                  {proveedoresSugeridos.map(prov => (
                    <button
                      key={prov.id_proveedor}
                      type="button"
                      onClick={() => seleccionarProveedor(prov)}
                      className="w-full px-3 py-2 text-left hover:bg-cyan-50 text-sm flex justify-between items-center border-b border-gray-100 last:border-0"
                    >
                      <span className="font-medium">{prov.nombre}</span>
                      <span className="text-xs text-gray-400">
                        {categorias.find(c => c.id_categoria === prov.categoria_id)?.nombre}
                      </span>
                    </button>
                  ))}
                  {proveedorBusqueda.trim() && (
                    <button
                      type="button"
                      onClick={abrirModalNuevoProveedor}
                      className="w-full px-3 py-2 text-left hover:bg-green-50 text-sm flex items-center gap-2 text-green-600 bg-green-50/50"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Crear "{proveedorBusqueda.trim()}" como nuevo proveedor</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Medio de Pago */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[#0E3A53]">Medio de Pago</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setMostrarModalMedioPago(true)}
                  className="text-[#1AA1D8] text-xs h-auto py-0 hover:text-[#0E3A53]"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Nuevo
                </Button>
              </div>
              <Select 
                value={formData.medio_pago_id || '__ninguno__'} 
                onValueChange={(value) => setFormData({ ...formData, medio_pago_id: value === '__ninguno__' ? '' : value })}
              >
                <SelectTrigger data-testid="select-medio-pago">
                  <SelectValue placeholder="Seleccionar medio de pago" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__ninguno__">-- Sin especificar --</SelectItem>
                  {[...mediosPago].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(medio => (
                    <SelectItem key={medio.id_medio_pago} value={medio.id_medio_pago}>{medio.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notas */}
            <div>
              <Label htmlFor="notas" className="text-[#0E3A53]">Notas</Label>
              <Input
                id="notas"
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                placeholder="Descripción o detalles adicionales"
              />
            </div>

            {/* Comprobante */}
            <div>
              <Label className="text-[#0E3A53]">Comprobante (PDF o imagen)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setArchivoComprobante(e.target.files[0])}
                  className="cursor-pointer"
                />
                {archivoComprobante && (
                  <span className="text-sm text-green-600">✓ {archivoComprobante.name}</span>
                )}
              </div>
              {gastoEditando?.comprobante_url && !archivoComprobante && (
                <p className="text-sm text-[#1AA1D8] mt-1">✓ Ya tiene un comprobante adjunto</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose} data-testid="cancel-btn">
              Cancelar
            </Button>
            <Button 
              onClick={onGuardar}
              className="bg-gradient-to-r from-[#FF7F6A] to-[#FF5C3A] hover:opacity-90"
              data-testid="save-btn"
              disabled={!formData.fecha || !formData.proveedor || !formData.monto}
            >
              {gastoEditando ? 'Guardar Cambios' : 'Crear Gasto'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Nuevo Proveedor */}
      <Dialog open={mostrarModalProveedor} onOpenChange={setMostrarModalProveedor}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53]">
              Nuevo Proveedor
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#0E3A53]">Nombre del Proveedor *</Label>
              <Input
                value={nuevoProveedorNombre}
                onChange={(e) => setNuevoProveedorNombre(e.target.value)}
                placeholder="Ej: Empresa ABC"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[#0E3A53]">Categoría *</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setMostrarModalCategoria(true)}
                  className="text-[#1AA1D8] text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Nueva Categoría
                </Button>
              </div>
              <Select 
                value={nuevoProveedorCategoria} 
                onValueChange={setNuevoProveedorCategoria}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar categoría" />
                </SelectTrigger>
                <SelectContent>
                  {[...categorias].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(cat => (
                    <SelectItem key={cat.id_categoria} value={cat.id_categoria}>
                      {cat.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setMostrarModalProveedor(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={crearNuevoProveedor}
              className="bg-[#1AA1D8] hover:bg-[#1590c2]"
              disabled={!nuevoProveedorNombre.trim() || !nuevoProveedorCategoria}
            >
              Crear Proveedor
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Nueva Categoría */}
      <Dialog open={mostrarModalCategoria} onOpenChange={setMostrarModalCategoria}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53]">
              Nueva Categoría
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#0E3A53]">Nombre de la Categoría *</Label>
              <Input
                value={nuevaCategoriaNombre}
                onChange={(e) => setNuevaCategoriaNombre(e.target.value)}
                placeholder="Ej: Servicios básicos"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setMostrarModalCategoria(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={crearNuevaCategoria}
              className="bg-[#1AA1D8] hover:bg-[#1590c2]"
              disabled={!nuevaCategoriaNombre.trim()}
            >
              Crear Categoría
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Nuevo Medio de Pago */}
      <Dialog open={mostrarModalMedioPago} onOpenChange={setMostrarModalMedioPago}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53]">
              Nuevo Medio de Pago
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#0E3A53]">Nombre del Medio de Pago *</Label>
              <Input
                value={nuevoMedioPagoNombre}
                onChange={(e) => setNuevoMedioPagoNombre(e.target.value)}
                placeholder="Ej: Transferencia, Efectivo, etc."
                onKeyPress={(e) => e.key === 'Enter' && crearNuevoMedioPago()}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setMostrarModalMedioPago(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={crearNuevoMedioPago}
              className="bg-[#1AA1D8] hover:bg-[#1590c2]"
              disabled={!nuevoMedioPagoNombre.trim()}
            >
              Crear Medio de Pago
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GastoForm;
